import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import {
  ApiVisa,
  BranchLocationLinkService,
  BranchService,
  LocationContainerModel,
  LocationService,
  Pagination,
} from 'blg-akaun-ts-lib';
import { ReplaySubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DropDownService } from '../../services/drop-down-service';

@Component({
  selector: 'blg-select-location-drop-down-latest',
  templateUrl: './blg-select-location-drop-down-latest.component.html',
  styleUrls: ['./blg-select-location-drop-down-latest.component.css'],
})
export class SelectLocationDropDownLatestComponent implements OnInit {
  @Input() location = new FormControl(null, Validators.required);
  @Input() apiVisa: ApiVisa;
  @Input() required: boolean;
  @Input() branchSelectedGuid: string;
  @Input() label = "Location";
  @Input() controlName: string;

  @Output() locationChange = new EventEmitter<FormControl>();
  @Output() locationSelected = new EventEmitter<LocationContainerModel>();

  serverSideFiltering = new FormControl();
  serverSideSearching = false;
  filteredOptions$: ReplaySubject<LocationContainerModel[]> = new ReplaySubject<
    LocationContainerModel[]
  >(1);
  locationList: LocationContainerModel[] = [];
  selectedLocation = new LocationContainerModel();
  locations: any = [];

  protected _onDestroy = new Subject<void>();
  unlinkedLocation: boolean;

  constructor(
    private lctnService: LocationService,
    private branchLocationLinkService: BranchLocationLinkService,
    private branchService: BranchService,
    private dropDownService: DropDownService
  ) {}

  ngOnInit() {
    // listen for search field value changes
    this.serverSideFiltering.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.LocationFiltering();
      });


    // load the initial location list
    this.getAllLocations();
    this.location.markAsTouched();
  }

  ngOnChanges() {
    if (this.branchSelectedGuid) {
      this.getAllLocations();
      
      // Set default location when the branch changes
      
      // this.setDefaultLocation();
    }
  }

  async setDefaultLocation() {
    if (this.branchSelectedGuid) {
      let defaultLocationGuid;
      // Fetch the default location based on the branch
      await this.branchService.getByGuid(this.branchSelectedGuid, this.apiVisa)
        .subscribe((branch) => {
          // Logic to determine the defaultLocationGuid
          if (branch.data.bl_fi_mst_branch?.location_guid) {
            defaultLocationGuid = branch.data.bl_fi_mst_branch.location_guid;
          } else if (
            branch.data.bl_fi_mst_branch_ext.find(
              (x) => x.param_code === "MAIN_LOCATION"
            )
          ) {
            defaultLocationGuid = branch.data.bl_fi_mst_branch_ext.find(
              (x) => x.param_code === "MAIN_LOCATION"
            )?.value_string;
          }
          
          // Set the defaultLocation in the form control
          if (defaultLocationGuid) {
            this.location.setValue(defaultLocationGuid);
          }
        });
    }
  }

  async getAllLocations() {
    // Reset lists and options
    this.locationList = [];
    this.locations = [];
    this.filteredOptions$.next(null);
  
    if (!this.branchSelectedGuid) {
      return;
    }
  
    try {
      // Retrieve branch details
      const branchResponse = await this.branchService.getByGuid(this.branchSelectedGuid, this.apiVisa).toPromise();
      const branchData = branchResponse?.data?.bl_fi_mst_branch;
      const branchExtData = branchResponse?.data?.bl_fi_mst_branch_ext || [];
      let defaultLocationGuid = branchData?.location_guid || branchExtData.find(x => x.param_code === "MAIN_LOCATION")?.value_string;
  
      // Get list of linked locations for the branch
      let pagination = new Pagination();
      pagination.limit = 1000;
      pagination.conditionalCriteria = [];
      pagination.conditionalCriteria.push({
        columnName: "guid_branch",
        operator: "=",
        value: this.branchSelectedGuid,
      });
      
      const branchLocationsResponse = await this.branchLocationLinkService.getByCriteria(pagination, this.apiVisa).toPromise();
      const locationGuidsList = branchLocationsResponse?.data
        .filter(loc => loc.bl_fi_mst_branch_location_link.guid_location)
        .map(loc => loc.bl_fi_mst_branch_location_link.guid_location.toString());
  
      // Retrieve all available locations with filtering criteria
      const formData = { limit: null, orderBy: "code", order: "ASC" };
      const locationsResponse = await this.dropDownService.get("location", formData, this.apiVisa).toPromise();
      const locationsData = locationsResponse?.data || [];
  
      // Apply location filter
      this.locationList = locationGuidsList.length > 0 ? locationsData.filter(loc => locationGuidsList.includes(loc.guid)) : locationsData;
      this.locations = [...this.locationList];
  
      // Conditionally set location value
      if (!this.location.value) {
        if (this.locations.length === 1) {
          // Automatically set if there's only one location linked
          this.location.setValue(this.locations[0].guid);
        } else {
          const isLocationInList = this.locations.some(loc => loc.guid === this.location.value);
          if (!isLocationInList) {
            // Set to default if not found in list and defaultLocationGuid exists
            const defaultLocation = this.locations.find(loc => loc.guid === defaultLocationGuid);
            if (defaultLocation) {
              this.location.setValue(defaultLocation.guid);
            }
          }
        }
      }
  
      // Check if location is unlinked
      this.unlinkedLocation = !this.locations.some(item => item.guid === this.location.value);
  
      // Sort and emit the updated list to display
      this.locations.sort((a, b) => a.code.localeCompare(b.code));
      this.filteredOptions$.next([...this.locations]);
    } catch (error) {
      console.error("Error in getAllLocations:", error);
    }
  }  

  LocationFiltering() {
    if (!this.locations) {
      return;
    }
    // get the search keyword
    let search = this.serverSideFiltering.value;
    if (!search) {
      this.filteredOptions$.next(this.locations.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    // filter the locations
    this.filteredOptions$.next(
      this.locations.filter(
        (option) =>
          (option.code
            ? option.code.toLowerCase().indexOf(search) > -1
            : null) ||
          (option.name ? option.name.toLowerCase().indexOf(search) > -1 : null)
      )
    );
  }

  onSelect(locationGuid: string) {
    if (locationGuid) {
      this.location.patchValue(locationGuid);
      this.lctnService
        .getByGuid(locationGuid, this.apiVisa)
        .subscribe((response) => {
          this.selectedLocation = response.data;
          this.locationSelected.emit(this.selectedLocation);
        });
    }
  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }
}
